import { useState, useEffect, useMemo, useRef } from "react";
import { Loader2, Bell, WifiOff, ClipboardList } from "lucide-react";
import { LoginPage, SessionExpiredModal } from "./components/AuthScreens";
import { OnboardingTour } from "./components/OnboardingTour";
import { ProfileAvatar } from "./components/ProfileAvatar";
import { setAskAboutPersonHandler } from "./lib/askAbout";
import {
  FIELDIQ_API_URL,
  authFetch,
  getToken,
  setToken,
  settleReauth,
} from "./lib/api";
import {
  flushSyncQueue,
  saveStopsToCache,
  saveWalksToCache,
} from "./lib/offline";
import { parseFacilitySlug } from "./lib/helpers";
import { applyFacilityManifest, rememberFacility } from "./lib/pwa";
import { pushSubscribe, pushUnsubscribe, pushUpdatePrefs } from "./lib/push";
import { ActivitiesTab, mapApiEvent, mapApiWalk } from "./tabs/ActivitiesTab";
import { BroadcastsTab } from "./tabs/BroadcastsTab";
import { ChatTab } from "./tabs/ChatTab";
import { LookupTab } from "./tabs/LookupTab";
import SitesTab from "./tabs/SitesTab";
import AssetsTab from "./tabs/AssetsTab";
import { PeopleTab } from "./tabs/PeopleTab";
import { ProfileTab } from "./tabs/ProfileTab";
import { ThreadsTab } from "./tabs/ThreadsTab";
import { TodayTab } from "./tabs/TodayTab";
import { BuildIQWorkTab } from "./tabs/BuildIQWorkTab";
import { TopicDetailView, TopicsTab } from "./tabs/TopicsTab";
import { getTabsForFacility, isFeatureEnabled } from "./tabs/tabRegistry";

// ═══════════════════════════════════════════════════════════════════
// APP SHELL
// ═══════════════════════════════════════════════════════════════════

const AppShell = ({ user, facilities, onLogout }) => {
  const [tab, setTab] = useState("today");
  // Intro tour — launched ONLY from Profile ("Watch Intro Tour"), never on
  // first run. It used to auto-open for any device without the onboarded
  // flag; that flag is gone with it, since nothing tracks a first run now.
  const [showOnboarding, setShowOnboarding] = useState(false);
  // Ref mirror so the broadcasts poll can check the active tab without
  // restarting its interval on every tab change.
  const tabRef = useRef(tab);
  tabRef.current = tab;
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [chatPrefill, setChatPrefill] = useState(null);
  const [chatAutoSend, setChatAutoSend] = useState(false);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(() => {
    // Prefer photo from the user object (already fetched from DB on login/reload);
    // fall back to localStorage so the avatar renders instantly on repeat visits.
    if (user?.photo_url) return user.photo_url;
    try {
      return localStorage.getItem("fieldiq_profile_photo") || null;
    } catch {
      return null;
    }
  });
  const [totalInteractions, setTotalInteractions] = useState(0);
  const [locCoords, setLocCoords] = useState(null);
  const [topicPrefs, setTopicPrefs] = useState(() => {
    try {
      const raw = localStorage.getItem("fieldiq_topic_prefs");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [syncToast, setSyncToast] = useState(null);

  // ── Push notifications ───────────────────────────────────────────
  const [pushPermission, setPushPermission] = useState(() =>
    "Notification" in window ? Notification.permission : "denied",
  );
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushPrefs, setPushPrefs] = useState(() => {
    try {
      const raw = localStorage.getItem("fieldiq_push_prefs");
      return raw
        ? JSON.parse(raw)
        : {
            events: true,
            walks: true,
            broadcasts: true,
            teams: true,
            kb: false,
          };
    } catch {
      return {
        events: true,
        walks: true,
        broadcasts: true,
        teams: true,
        kb: false,
      };
    }
  });

  // Check existing subscription on mount (SW already registered in App)
  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => {
        if (sub) setPushSubscribed(true);
      })
      .catch(() => {});

    // Handle SW-triggered re-subscribe (subscription expiry)
    const handleMessage = (e) => {
      if (e.data?.type === "PUSH_RESUBSCRIBE") {
        pushSubscribe(user?.id).then((sub) => {
          if (sub) setPushSubscribed(true);
        });
      }
      if (e.data?.type === "PUSH_NAVIGATE" && e.data.tab) {
        setTab(e.data.tab);
      }
    };
    navigator.serviceWorker.addEventListener("message", handleMessage);
    return () =>
      navigator.serviceWorker.removeEventListener("message", handleMessage);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEnablePush = async () => {
    if (!("Notification" in window)) return;
    const perm = await Notification.requestPermission();
    setPushPermission(perm);
    if (perm !== "granted") return;
    const sub = await pushSubscribe(user?.id);
    if (sub) setPushSubscribed(true);
  };

  const handleDisablePush = async () => {
    await pushUnsubscribe();
    setPushSubscribed(false);
  };

  const handlePushPrefsChange = async (prefs) => {
    setPushPrefs(prefs);
    try {
      localStorage.setItem("fieldiq_push_prefs", JSON.stringify(prefs));
    } catch {}
    await pushUpdatePrefs(prefs);
  };
  // ────────────────────────────────────────────────────────────────

  // ── Broadcasts state declared below facility (avoids TDZ) ────────

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      flushSyncQueue((synced, failed) => {
        if (synced > 0) {
          const msg =
            synced === 1 ? "1 change synced" : `${synced} changes synced`;
          setSyncToast(failed > 0 ? `${msg} (${failed} failed)` : msg);
        }
      });
    };
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    // Flush any writes from a previous session on app open
    flushSyncQueue();
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!syncToast) return;
    const t = setTimeout(() => setSyncToast(null), 3500);
    return () => clearTimeout(t);
  }, [syncToast]);

  const handleTopicPrefsChange = (prefs) => {
    setTopicPrefs(prefs);
    try {
      localStorage.setItem("fieldiq_topic_prefs", JSON.stringify(prefs));
    } catch {}
  };

  // Sync photo from user object whenever it changes (covers login + me-reload)
  useEffect(() => {
    if (user?.photo_url) {
      setProfilePhotoUrl(user.photo_url);
      try {
        localStorage.setItem("fieldiq_profile_photo", user.photo_url);
      } catch {}
    }
  }, [user?.photo_url]);

  useEffect(() => {
    authFetch("/fieldiq/profile")
      .then((data) => {
        if (data?.photo_url) {
          setProfilePhotoUrl(data.photo_url);
          try {
            localStorage.setItem("fieldiq_profile_photo", data.photo_url);
          } catch {}
        }
        if (typeof data?.total_interactions === "number") {
          setTotalInteractions(data.total_interactions);
        }
      })
      .catch(() => {});
  }, [user?.id]);

  const facility = facilities?.[0] || null;
  const tabs = getTabsForFacility(facility);
  // Topics surfaces in three places: its own tab, the Today shortcuts, and the
  // Profile picker. The nav is the single source of truth — a facility with
  // Topics switched off should see none of them, and the Today shortcuts in
  // particular would otherwise open a detail view for a tab that is not there.
  const topicsEnabled = tabs.some((t) => t.id === "topics");
  // In-tab feature switch, not a nav tab — see tabRegistry. Facilities that do
  // not canvass keep the Today tab without the interaction logger inside it.
  const logInteractionEnabled = isFeatureEnabled(
    facility,
    "today_log_interaction",
  );

  // /my-services is a round trip away on first paint, and falling back to the
  // Hyve mark meant every load flashed the wrong brand before settling. The
  // logo was already cached for the login screen — use it here too.
  const cachedLogo = useMemo(() => {
    try {
      const slug = parseFacilitySlug();
      return slug ? localStorage.getItem(`fieldiq_logo_${slug}`) : null;
    } catch {
      return null;
    }
  }, []);

  // ── Broadcasts (pinned + unread) — must follow `facility` declaration ──
  const [pinnedBroadcasts, setPinnedBroadcasts] = useState([]);
  const [broadcastsUnread, setBroadcastsUnread] = useState(0);

  useEffect(() => {
    const ldId = facility?.facility_id;
    if (!ldId) return;
    const load = () => {
      if (!navigator.onLine) return;
      fetch(`${FIELDIQ_API_URL}/broadcasts?ld_id=${encodeURIComponent(ldId)}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (!data) return;
          setPinnedBroadcasts(data.pinned || []);
          // Don't re-raise the badge while the user is viewing the tab —
          // it clears the badge itself and marks items read individually.
          if (tabRef.current !== "broadcasts")
            setBroadcastsUnread(data.unread_count || 0);
        })
        .catch(() => {});
    };
    load();
    // Near-realtime announcements: poll every 60s while visible,
    // and refresh immediately when the PWA returns to the foreground.
    const id = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 60e3);
    const onVis = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [facility?.facility_id]); // eslint-disable-line react-hooks/exhaustive-deps
  // ────────────────────────────────────────────────────────────────

  const [facilityAlerts, setFacilityAlerts] = useState([]);
  const [alertModalOpen, setAlertModalOpen] = useState(false);

  useEffect(() => {
    const fid = facility?.facility_id;
    if (!fid) return;
    authFetch(`/fieldiq/facility-alerts?facility_id=${encodeURIComponent(fid)}`)
      .then((data) => {
        if (Array.isArray(data))
          setFacilityAlerts(data.filter((a) => a.is_active));
      })
      .catch(() => {});
  }, [facility?.facility_id]);

  const [apiTopics, setApiTopics] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  useEffect(() => {
    // Nothing renders topics when the tab is off, so skip the round trip —
    // this is a mobile PWA and every request on load costs.
    if (!topicsEnabled) {
      setApiTopics([]);
      return;
    }
    const fid = facility?.facility_id || "";
    const qs = fid ? `?facility_id=${encodeURIComponent(fid)}` : "";
    authFetch(`/fieldiq/topics${qs}`)
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) setApiTopics(data);
      })
      .catch(() => {});
  }, [facility, topicsEnabled]);

  // Pre-fetch walks + stops for offline use; also extract upcoming events for TodayTab
  useEffect(() => {
    const fid = facility?.facility_id;
    if (!fid || !navigator.onLine) return;
    const district = facility?.ld_district;
    const qs = new URLSearchParams();
    if (district) qs.set("district", district);
    qs.set("facility_id", fid);
    authFetch(`/fieldiq/activities?${qs}`)
      .then((data) => {
        if (!data) return;
        if (Array.isArray(data.events)) {
          const events = data.events
            .map(mapApiEvent)
            .filter((e) => e.status === "upcoming")
            .slice(0, 3);
          setUpcomingEvents(events);
        }
        if (!data.walks) return;
        const walks = data.walks.map(mapApiWalk);
        saveWalksToCache(walks);
        walks
          .filter(
            (w) =>
              w.status === "in_progress" && w._apiId && w.waypoints?.length,
          )
          .forEach((w) => {
            authFetch(`/fieldiq/walks/${w._apiId}/stops`)
              .then((stops) => {
                if (Array.isArray(stops)) saveStopsToCache(w._apiId, stops);
              })
              .catch(() => {});
          });
      })
      .catch(() => {});
  }, [facility]); // eslint-disable-line react-hooks/exhaustive-deps

  const openTopic = (topic) => {
    setSelectedTopic(topic);
    setTab("topics");
  };

  const askLidy = (topicLabel) => {
    setChatPrefill(`Tell me about ${topicLabel}`);
    setChatAutoSend(true);
    setSelectedTopic(null);
    setTab("chat");
  };

  const askAboutPerson = (name) => {
    setChatPrefill(`Tell me about ${name}`);
    setChatAutoSend(true);
    setTab("chat");
  };

  useEffect(() => {
    setAskAboutPersonHandler(askAboutPerson);
    return () => {
      setAskAboutPersonHandler(null);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabSwitch = (newTab) => {
    if (newTab !== "topics") setSelectedTopic(null);
    setTab(newTab);
  };

  const goToLookupWithCoords = (coords) => {
    setLocCoords(coords);
    handleTabSwitch("lookup");
  };

  const renderTab = () => {
    if (tab === "today")
      return (
        <TodayTab
          user={user}
          facility={facility}
          onOpenTopic={openTopic}
          onLocReady={goToLookupWithCoords}
          topicPrefs={topicPrefs}
          apiTopics={apiTopics}
          topicsEnabled={topicsEnabled}
          logInteractionEnabled={logInteractionEnabled}
          isOnline={isOnline}
          upcomingEvents={upcomingEvents}
          pinnedBroadcasts={pinnedBroadcasts}
          onViewBroadcasts={() => handleTabSwitch("broadcasts")}
        />
      );
    if (tab === "chat")
      return (
        <ChatTab
          user={user}
          facility={facility}
          prefill={chatPrefill}
          onClearPrefill={() => setChatPrefill(null)}
          autoSend={chatAutoSend}
          onClearAutoSend={() => setChatAutoSend(false)}
          isOnline={isOnline}
        />
      );
    if (tab === "people")
      return <PeopleTab user={user} facility={facility} isOnline={isOnline} />;
    if (tab === "topics") {
      if (selectedTopic)
        return (
          <TopicDetailView
            topic={selectedTopic}
            onBack={() => setSelectedTopic(null)}
            onAskLidy={askLidy}
          />
        );
      return (
        <TopicsTab
          facility={facility}
          onOpenTopic={setSelectedTopic}
          isOnline={isOnline}
        />
      );
    }
    if (tab === "activities")
      return (
        <ActivitiesTab user={user} facility={facility} isOnline={isOnline} />
      );
    if (tab === "lookup")
      return (
        <LookupTab
          user={user}
          facility={facility}
          onAskAbout={askAboutPerson}
          initialCoords={locCoords}
          onConsumeCoords={() => setLocCoords(null)}
          isOnline={isOnline}
        />
      );
    // Construction's site lookup is its OWN tab — never the political voter/
    // district/candidate lookup (that bled across when both shared LookupTab).
    if (tab === "site_lookup")
      return (
        <SitesTab
          facility={facility}
          onAskAbout={askAboutPerson}
          isOnline={isOnline}
        />
      );
    // Construction equipment/yards/units + their maintenance plans (read-only,
    // lease-redacted — reads the crew BuildIQ asset registry).
    if (tab === "equipment") return <AssetsTab isOnline={isOnline} />;
    if (tab === "job_routes")
      return (
        <ActivitiesTab user={user} facility={facility} isOnline={isOnline} />
      );
    if (tab === "bob")
      return (
        <ChatTab
          user={user}
          facility={facility}
          prefill={chatPrefill}
          onClearPrefill={() => setChatPrefill(null)}
          autoSend={chatAutoSend}
          onClearAutoSend={() => setChatAutoSend(false)}
          isOnline={isOnline}
        />
      );
    if (tab === "work_log")
      return (
        <BuildIQWorkTab user={user} facility={facility} isOnline={isOnline} />
      );
    if (tab === "bid" || tab === "materials")
      return (
        <div className="flex flex-col items-center justify-center h-64 gap-3 text-slate-500">
          <ClipboardList size={36} />
          <p className="text-sm font-medium">Coming soon</p>
        </div>
      );
    if (tab === "broadcasts")
      return (
        <>
          <BroadcastsTab
            user={user}
            facility={facility}
            isOnline={isOnline}
            onUnreadChange={(n) => setBroadcastsUnread(n)}
          />
          <div className="mt-6 pt-4 border-t border-slate-800">
            <ThreadsTab user={user} facility={facility} isOnline={isOnline} />
          </div>
        </>
      );
    if (tab === "profile")
      return (
        <ProfileTab
          user={user}
          facility={facility}
          onLogout={onLogout}
          onPhotoChange={setProfilePhotoUrl}
          topicPrefs={topicPrefs}
          onTopicPrefsChange={handleTopicPrefsChange}
          apiTopics={apiTopics}
          topicsEnabled={topicsEnabled}
          totalInteractions={totalInteractions}
          pushPermission={pushPermission}
          pushSubscribed={pushSubscribed}
          pushPrefs={pushPrefs}
          onEnablePush={handleEnablePush}
          onDisablePush={handleDisablePush}
          onPushPrefsChange={handlePushPrefsChange}
          onReplayTour={() => setShowOnboarding(true)}
        />
      );
  };

  // Chat manages its own scroll; all other tabs scroll in main
  const isChat = tab === "chat";

  return (
    // Outer div fills entire screen with dark bg (visible on desktop beside the column)
    <div className="bg-slate-950" style={{ minHeight: "100dvh" }}>
      {showOnboarding && (
        <OnboardingTour
          onDone={() => setShowOnboarding(false)}
          onEnablePush={handleEnablePush}
          showPushSlide={
            "Notification" in window &&
            pushPermission === "default" &&
            !pushSubscribed
          }
        />
      )}
      {/* Column: centered, exact viewport height, no outer scroll */}
      <div
        className="max-w-lg mx-auto flex flex-col overflow-hidden"
        style={{ height: "100dvh" }}
      >
        <header className="flex items-center justify-between px-4 pt-1.5 pb-2.5 border-b border-slate-800 bg-slate-900 flex-shrink-0">
          <div className="flex items-center gap-3">
            <img
              src={
                facility?.logo_url ||
                facility?.services?.find((s) => s.service_type === "rag")
                  ?.config?.logo_url ||
                cachedLogo ||
                "/logo-white.png"
              }
              alt="FieldIQ"
              className="h-7 object-contain"
              onError={(e) => {
                e.currentTarget.src = "/logo-white.png";
              }}
            />
            <span className="text-white font-semibold text-sm">FieldIQ</span>
          </div>
          <div className="flex items-center gap-2">
            {!isOnline && (
              <span
                className="flex items-center gap-1 text-amber-400 text-xs"
                title="No connection — changes saved locally"
              >
                <WifiOff size={14} />
              </span>
            )}
            {syncToast && (
              <span className="text-emerald-400 text-xs font-medium">
                {syncToast}
              </span>
            )}
            {facilityAlerts.length > 0 && (
              <button
                onClick={() => setAlertModalOpen(true)}
                className="relative p-1.5 rounded-full text-amber-400 hover:bg-slate-800 transition-colors"
                aria-label="View alerts"
              >
                <Bell size={18} />
                <span className="absolute top-0 right-0 h-2 w-2 rounded-full bg-amber-400" />
              </button>
            )}
            <ProfileAvatar
              user={user}
              photoUrl={profilePhotoUrl}
              onClick={() => handleTabSwitch("profile")}
            />
            <button
              onClick={() => handleTabSwitch("broadcasts")}
              className="relative p-1.5 rounded-full hover:bg-slate-800 transition-colors"
              aria-label="News"
            >
              <Bell
                size={18}
                className={
                  broadcastsUnread > 0 ? "text-cyan-400" : "text-slate-500"
                }
              />
              {broadcastsUnread > 0 && (
                <span className="absolute top-0 right-0 min-w-[15px] h-[15px] px-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none">
                  {broadcastsUnread > 9 ? "9+" : broadcastsUnread}
                </span>
              )}
            </button>
          </div>
        </header>

        {alertModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 px-4 pb-8"
            onClick={() => setAlertModalOpen(false)}
          >
            <div
              className="w-full max-w-lg bg-slate-900 border border-slate-700 rounded-2xl p-5 space-y-3"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <h2 className="text-white font-semibold text-base flex items-center gap-2">
                  <Bell size={16} className="text-amber-400" /> Alerts
                </h2>
                <button
                  onClick={() => setAlertModalOpen(false)}
                  className="text-slate-400 hover:text-white text-xl leading-none"
                >
                  ×
                </button>
              </div>
              {facilityAlerts.map((alert) => (
                <div
                  key={alert.id}
                  className="bg-amber-950/40 border border-amber-700/50 rounded-xl px-4 py-3 text-sm text-amber-200 leading-snug"
                >
                  {alert.content}
                </div>
              ))}
            </div>
          </div>
        )}

        <nav className="flex border-b border-slate-800 bg-slate-900 flex-shrink-0">
          {tabs.map(({ id, label, icon: Icon }) => {
            const tabLabel =
              id === "chat" || id === "bob"
                ? facility?.agent_name || label
                : label;
            const badge =
              id === "broadcasts" && broadcastsUnread > 0
                ? broadcastsUnread
                : 0;
            return (
              <button
                key={id}
                onClick={() => handleTabSwitch(id)}
                className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 text-xs font-medium transition-colors ${
                  tab === id
                    ? "text-cyan-400 border-b-2 border-cyan-500"
                    : "text-slate-500 hover:text-slate-300"
                }`}
              >
                <span className="relative">
                  <Icon size={17} />
                  {badge > 0 && (
                    <span className="absolute -top-1 -right-2 min-w-[14px] h-3.5 px-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none">
                      {badge > 99 ? "99+" : badge}
                    </span>
                  )}
                </span>
                {tabLabel}
              </button>
            );
          })}
        </nav>

        <main
          className={
            isChat
              ? "flex-1 overflow-hidden flex flex-col"
              : "flex-1 overflow-y-auto px-4 py-4"
          }
        >
          {renderTab()}
        </main>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// ROOT
// ═══════════════════════════════════════════════════════════════════

// Pick the best facility from the list given an optional URL slug.
// Falls back to the first facility if no slug matches.
const pickFacility = (facilities, slug) => {
  if (!facilities.length) return null;
  if (!slug) return facilities[0];
  const match = facilities.find(
    (f) => f.facility_id?.toUpperCase() === slug.toUpperCase(),
  );
  return match || facilities[0];
};

export default function App() {
  const [user, setUser] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [facilitySlug] = useState(() => parseFacilitySlug());
  const userRef = useRef(null);
  userRef.current = user;

  // Register service worker once on app load (needed for push + offline)
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => {});
    }
  }, []);

  // A 401 anywhere pauses the request and fires this event. If the user
  // isn't logged in yet (stale token on the initial /me check), fall
  // through to the login page; otherwise show the re-login modal so
  // in-progress work is preserved.
  useEffect(() => {
    const onExpired = () => {
      if (!userRef.current) {
        setToken(null);
        settleReauth(false);
        setLoading(false);
        return;
      }
      setSessionExpired(true);
    };
    window.addEventListener("fieldiq:session-expired", onExpired);
    return () =>
      window.removeEventListener("fieldiq:session-expired", onExpired);
  }, []);

  useEffect(() => {
    const tok = getToken();
    if (!tok) {
      setLoading(false);
      return;
    }
    if (!navigator.onLine) {
      try {
        const cached = localStorage.getItem("fieldiq_user_cache");
        if (cached) setUser(JSON.parse(cached));
      } catch {}
      setLoading(false);
      return;
    }
    authFetch("/me")
      .then((data) => {
        if (data.user) {
          setUser(data.user);
          try {
            localStorage.setItem(
              "fieldiq_user_cache",
              JSON.stringify(data.user),
            );
          } catch {}
        } else {
          setToken(null);
        }
      })
      .catch(() => setToken(null))
      .finally(() => setLoading(false));
  }, []);

  // Fetch facilities after login — same call as client portal
  useEffect(() => {
    if (!user) return;
    if (!navigator.onLine) {
      try {
        const cached = localStorage.getItem("fieldiq_facilities_cache");
        if (cached) setFacilities(JSON.parse(cached));
      } catch {}
      return;
    }
    authFetch("/my-services").then((data) => {
      if (Array.isArray(data)) {
        setFacilities(data);
        try {
          localStorage.setItem(
            "fieldiq_facilities_cache",
            JSON.stringify(data),
          );
        } catch {}
      }
    });
  }, [user]);

  // Sync URL to the active facility after facilities are resolved,
  // and cache the logo URL so the login screen can use it on next visit.
  useEffect(() => {
    if (!facilities.length) return;
    const active = pickFacility(facilities, facilitySlug);
    if (active) {
      const target = `/${active.facility_id}`;
      if (window.location.pathname !== target) {
        window.history.replaceState(null, "", target);
      }
      // Canonical branding source: Client Portal > Knowledge Base > Customize >
      // Branding > Logo, stored on the rag service config and promoted to
      // facility.logo_url by /my-services. Never a per-app logo field — that
      // divergence is what made BuildIQ and FieldIQ show different logos.
      const logo =
        active.logo_url ||
        active.services?.find((s) => s.service_type === "rag")?.config
          ?.logo_url ||
        null;
      const name = active.facility_name || "";
      try {
        if (logo) {
          localStorage.setItem(`fieldiq_logo_${active.facility_id}`, logo);
        } else {
          localStorage.removeItem(`fieldiq_logo_${active.facility_id}`);
        }
        if (name)
          localStorage.setItem(`fieldiq_name_${active.facility_id}`, name);
      } catch {}
      // Re-apply with live branding — index.js already applied the cached
      // version before render so the browser had something facility-correct.
      rememberFacility(active.facility_id);
      applyFacilityManifest({
        facilityId: active.facility_id,
        name,
        logoUrl: logo,
      });
    }
  }, [facilities, facilitySlug]);

  const handleLogin = (u) => setUser(u);
  const handleLogout = () => {
    setSessionExpired(false);
    settleReauth(false); // fail any requests paused on the 401 gate
    setToken(null);
    setUser(null);
    setFacilities([]);
    // Preserve the facility slug in the URL so the next login stays on the same tenant
    const slug = parseFacilitySlug();
    if (!slug && facilitySlug) {
      window.history.replaceState(null, "", `/${facilitySlug}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center text-slate-400">
        <Loader2 size={24} className="animate-spin" />
      </div>
    );
  }

  const cachedLogoUrl = facilitySlug
    ? (() => {
        try {
          return localStorage.getItem(`fieldiq_logo_${facilitySlug}`) || null;
        } catch {
          return null;
        }
      })()
    : null;

  if (!user)
    return (
      <LoginPage
        onLogin={handleLogin}
        facilitySlug={facilitySlug}
        logoUrl={cachedLogoUrl}
      />
    );

  const activeFacility = pickFacility(facilities, facilitySlug);
  const orderedFacilities = activeFacility
    ? [activeFacility, ...facilities.filter((f) => f !== activeFacility)]
    : facilities;

  return (
    <>
      <AppShell
        user={user}
        facilities={orderedFacilities}
        onLogout={handleLogout}
      />
      {sessionExpired && (
        <SessionExpiredModal
          user={user}
          facilitySlug={activeFacility?.facility_id || facilitySlug}
          onSuccess={(u) => {
            if (u) {
              setUser(u);
              try {
                localStorage.setItem("fieldiq_user_cache", JSON.stringify(u));
              } catch {}
            }
            setSessionExpired(false);
            settleReauth(true); // paused requests retry with the new token
          }}
          onLogout={handleLogout}
        />
      )}
    </>
  );
}
